import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { activateAccount, resendActivationEmail } from '../api/api';
import { ArrowLeft, Mail, CheckCircle, AlertCircle } from 'lucide-react';

const Logo = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 7.5L12 4L20 7.5L12 11L4 7.5Z" fill="currentColor" />
    <path d="M4 11.5L12 15L20 11.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 15.5L12 19L20 15.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AccountActivation = () => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [showResendForm, setShowResendForm] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleActivation = useCallback(async (activationToken) => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      await activateAccount(activationToken);
      setSuccess('Conta ativada com sucesso! Redirecionando para o login...');
      setIsActivated(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      setError('Erro ao ativar conta. O token pode estar expirado ou inválido. Você pode solicitar um novo email de ativação abaixo.');
      setShowResendForm(true);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const handleResendActivation = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsResending(true);

    if (!email) {
      setError('Por favor, informe seu email.');
      setIsResending(false);
      return;
    }

    try {
      await resendActivationEmail(email);
      setSuccess('Se o email estiver cadastrado e a conta não estiver ativada, você receberá um novo link de ativação.');
      setEmail('');
    } catch (error) {
      setError('Erro ao reenviar email de ativação. Verifique se o email está correto e tente novamente.');
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      handleActivation(tokenFromUrl);
    } else {
      setError('Token de ativação não encontrado. Por favor, verifique o link ou solicite um novo email de ativação.');
      setShowResendForm(true);
    }
  }, [searchParams, handleActivation]);

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col items-center justify-center p-4 bg-grid">
      <div className="absolute top-8 left-8">
        <Link 
          to="/login"
          className="flex items-center gap-2 text-gray-500 hover:text-purple-600 font-bold transition-all group"
        >
          <div className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center group-hover:bg-purple-50 group-hover:border-purple-100 shadow-sm transition-all">
            <ArrowLeft size={18} />
          </div>
          <span>Voltar ao login</span>
        </Link>
      </div>

      <div className="w-full max-w-md animate-fade-in">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="flex items-center text-black mb-4">
            <Logo size={48} />
            <span className="font-display text-4xl ml-4 tracking-tighter">SORA</span>
          </div>
          <h1 className="text-3xl font-black mb-2 font-hero">
            Ativar Conta
          </h1>
          <p className="text-gray-500 font-medium">
            {isActivated ? 'Sua conta foi ativada!' : 'Ativando sua conta...'}
          </p>
        </div>

        <div className="bg-white p-8 md:p-10 rounded-[3rem] shadow-xl border border-gray-100">
          {success && (
            <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-xl">
              <p className="text-green-800 font-medium text-sm">{success}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl">
              <p className="text-red-800 font-medium text-sm">{error}</p>
            </div>
          )}

          {isLoading && !isActivated && (
            <div className="text-center py-8">
              <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">Ativando sua conta...</p>
            </div>
          )}

          {showResendForm && !isLoading && !isActivated && (
            <form onSubmit={handleResendActivation} className="space-y-5">
              <div>
                <label className="block text-sm font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                  Email da conta
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                    <Mail size={18} />
                  </div>
                  <input
                    type="email"
                    placeholder="exemplo@escola.com"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isResending}
                    className="w-full bg-gray-50 border-2 border-gray-100 focus:border-purple-200 rounded-2xl py-4 pl-12 pr-4 outline-none transition-all font-medium disabled:opacity-50"
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500 font-medium">
                  Digite o email usado para criar a conta para receber um novo link de ativação.
                </p>
              </div>

              <button 
                type="submit" 
                disabled={isResending}
                className={`w-full bg-black text-white font-display py-5 rounded-2xl text-lg shadow-xl shadow-gray-200 transition-all active:scale-95 flex items-center justify-center gap-3 ${isResending ? 'opacity-70 cursor-wait' : 'hover:bg-gray-800'}`}
              >
                {isResending ? (
                  <div className="w-6 h-6 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Mail size={20} />
                    Reenviar Email de Ativação
                  </>
                )}
              </button>
            </form>
          )}

          {!showResendForm && !isLoading && (
            <div className="mt-8 pt-8 border-t border-gray-50 text-center">
              <p className="text-gray-500 font-medium">
                <Link 
                  to="/login" 
                  className="text-purple-600 font-bold hover:underline"
                >
                  Voltar para o login
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
      
      <p className="mt-12 text-gray-400 text-sm font-medium">
        © 2025 Sôra — Todos os direitos reservados.
      </p>
    </div>
  );
};

export default AccountActivation;
